import { UnreachableError } from 'base/helpers';
import * as React from 'react';
import { Avatar } from './avatar';
import styles from './fav_icon.css';
import { COLOR_TOKENS } from './tokens';

const MAX_FAVICON_SIZE = 48;
const MIN_FAVICON_SIZE = 32;
// Size in pixels of the icon for when `size` is `small`.
const SMALL_SIZE = 24;

/**
 * Specifies how the fav icon will be sized:
 * - small: a 24x24 image will be rendered.
 * - dynamic: the size will be determined depending on availability,
 *   preferring images with more quality as long as they don't go over
 *   the `MAX_FAVICON_SIZE`.
 */
type FavIconSize = 'small' | 'dynamic';

type FavIconProps = {
  domain: string;
  size: FavIconSize;
};

/**
 * Given a domain this component will render its favicon.
 * MDN: https://developer.mozilla.org/en-US/docs/Glossary/Favicon
 * We use a service provided by Google to extract the favicon.
 * - Google: it's free and we can specify the desired size. Cons: it's a _hidden_ API.
 *   Example: https://www.google.com/s2/favicons?domain=stackoverflow.com&sz=512
 *
 * Other potential alternatives in case we find Google's service insufficient:
 * - Duck Duck Go: It's free but we cannot specify the desired size. Also a _hidden_ API.
 *   Example URL: https://icons.duckduckgo.com/ip3/www.stackoverflow.com.ico
 * - icon.horse: Paid service with a free tier. It supports sizes and reasonable fallbacks.
 *   https://icon.horse
 *   Example URL: https://icon.horse/icon/discord.com
 */
export function FavIcon({ domain, size }: FavIconProps) {
  const imgSrc = `https://www.google.com/s2/favicons?domain=${domain}&sz=${MAX_FAVICON_SIZE}`;
  const [status, setStatus] = React.useState<
    { kind: 'render-fallback' } | { kind: 'render-favicon'; size: number }
  >({ kind: 'render-fallback' });
  const imageRef = React.useRef<HTMLImageElement>(null);
  React.useEffect(() => {
    const image = new Image();
    function onLoad() {
      if (image.naturalHeight < MIN_FAVICON_SIZE) {
        setStatus({ kind: 'render-fallback' });
      } else {
        const imageSize = size === 'small' ? SMALL_SIZE : image.naturalHeight;
        setStatus({ kind: 'render-favicon', size: imageSize });
      }
    }
    image.src = imgSrc;
    image.addEventListener('load', onLoad);

    return () => image.removeEventListener('load', onLoad);
  }, [domain, imgSrc]);

  const favIconHeight = size === 'small' ? SMALL_SIZE : MAX_FAVICON_SIZE;

  return (
    // Setting the height on the wrapping `<div>` to ensure there's no visual jump when the final image size is set.
    <div className={styles.favIcon} style={{ height: favIconHeight }}>
      {status.kind === 'render-favicon' ? (
        <img
          ref={imageRef}
          src={imgSrc}
          width={status.size}
          height={status.size}
        />
      ) : (
        <Avatar
          size={favIconSizeToAvatarSize(size)}
          shape="square"
          color={COLOR_TOKENS.colorPurpleDark}
          firstName={domain}
        />
      )}
    </div>
  );
}

function favIconSizeToAvatarSize(size: FavIconSize) {
  switch (size) {
    case 'small':
      return 'small';
    case 'dynamic':
      return 'xlarge';
    default: {
      throw new UnreachableError(size);
    }
  }
}
